<script setup lang="ts">
import {BlockProps} from "@/utils/presenters/interfaces.ts";
import EditViewSkeleton from "@/components/skeleton/EditViewSkeleton.vue";
import {computed, onBeforeMount, ref, Ref, useTemplateRef, watch} from "vue";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import ViewMode from "@/utils/constants/ViewMode.ts";
import EditedObjectSourceType from "@/utils/constants/EditedObjectSourceType.ts";
import EditViewUtils from "@/utils/editView/EditViewUtils.ts";
import EditViewLayout from "@/components/EditView/EditViewLayout.vue";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import Translator from "@/utils/common/Translator.ts";
import EditScreenEmbeddedEdit = namaTypes.EditScreenEmbeddedEdit;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import EntityReferenceData = namaTypes.EntityReferenceData;

const props = defineProps<BlockProps>();
const blockRef = computed<EditScreenEmbeddedEdit>(() => props.block as EditScreenEmbeddedEdit);
const editViewLayoutRef = useTemplateRef<InstanceType<typeof EditViewLayout>>("editViewLayoutRef")
const layoutFetched: Ref<boolean> = ref(false);
const entityType: string = blockRef.value.entityType ?? '';
const object: Ref<BaseEntityDTO | undefined> = ref();
const emit = defineEmits<{ (e: 'handleHide'): void }>()

let place: EditPlace = new EditPlace();
place.entityType = entityType;
place.view = ViewMode.NEW;
place.editViewName = blockRef.value.viewName;
place.templateId = blockRef.value.defaultValuesTemplateId;
place.quickCreate = true;
let embeddedPresenter: EditPresenter = new EditPresenter(place);

embeddedPresenter.editScreenEmbeddedEditView = {
  block: blockRef.value,
  fetchContainerObject: () => props.presenter.fetchRecord(),
  handleHide
};
watch(() => props.presenter.fetchRecord()?.id, (id) => {
  handleHide();
}, {immediate: true});

const title = computed(() => editViewLayoutRef.value?.title)
const states = computed(() => editViewLayoutRef.value?.states)

onBeforeMount(async () => {
  if (!props.collapsed)
    await doStart();
});

watch(() => props.collapsed, async (collapsed) => {
  if (layoutFetched.value)
    props.presenter.embeddedEditViewList = props.presenter.embeddedEditViewList.filter((presenter) => presenter.fetchRecord().id !== embeddedPresenter.fetchRecord().id);
  layoutFetched.value = false;
  if (!collapsed)
    await doStart();
});

const fetchingLayout = ref(false);

async function doStart() {
  if (!layoutFetched.value && !fetchingLayout.value) {
    try {
      fetchingLayout.value = true;
      await start();
      layoutFetched.value = true;
    } catch (error) {
      layoutFetched.value = false;
    } finally {
      fetchingLayout.value = false;
    }
  }
}

async function start() {
  if (isNotAllowToView())
    return;
  fillObjectIfEditMode();
  embeddedPresenter.createdObject = object.value;
  await embeddedPresenter.start();
  props.presenter.embeddedEditViewList.push(embeddedPresenter)
}

function handleHide() {
  if (emit)
    emit('handleHide');
}

function fillObjectIfEditMode() {
  if (blockRef.value.editedObjectSourceType === EditedObjectSourceType.AlwaysCreateNew)
    return;
  if (blockRef.value.editedObjectSourceType === EditedObjectSourceType.FromField && blockRef.value.editedObjectField) {
    const field: EntityReferenceData = props.presenter.fetchRecord()[blockRef.value.editedObjectField] as EntityReferenceData;
    if (field) {
      embeddedPresenter.place.view = ViewMode.EDIT;
      embeddedPresenter.place.id = field.id;
      embeddedPresenter.place.entityType = field.entityType ?? entityType;
    } else {
      useErrorsStore().pushErrorStr(Translator.translateId("targetFieldIsEmpty"));
      handleHide();
    }
  }
  if (object.value && object.value.commitedBefore) {
    embeddedPresenter.place.view = ViewMode.EDIT;
    embeddedPresenter.place.id = object.value.id;
    embeddedPresenter.place.createdObjectId = object.value.id;
  }
}

function isNotAllowToView(): boolean {
  if (!blockRef.value.allowWhenContainerRecordIsSaved)
    return false;
  if (EditViewUtils.isNew(props.presenter.place) || props.presenter.isDirty()) {
    useErrorsStore().pushErrorStr(Translator.translateId("shouldCommitToOpenGroup"));
    handleHide();
    return true;
  }
  return false;
}

defineExpose({states, title});
</script>

<template>
  <div v-if="!collapsed" class="tw-h-[inherit] tw-w-full">
    <div v-if="embeddedPresenter.validLayout && layoutFetched" class="tw-h-[inherit] tw-w-full">
      <EditViewLayout ref="editViewLayoutRef" :presenter="embeddedPresenter" :place="place" :alwaysHideLabels="true" :showTitle="false"/>
    </div>
    <div v-else class="tw-h-[inherit] tw-w-full">
      <EditViewSkeleton/>
    </div>
  </div>
</template>

<style scoped>

</style>
